import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
    Dumbbell, Flower2, Sparkles, Salad, Waves, Zap, HandHeart, HeartPulse,
    MapPin, Phone, PhoneCall, CalendarCheck, Clock, ChevronRight, Instagram, Star,
    MessageCircle, MessageSquare, X, Send,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Button } from "../components/ui/button";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const goGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const IMG = {
    hero: "https://images.pexels.com/photos/6551133/pexels-photo-6551133.jpeg?auto=compress&cs=tinysrgb&w=1600",
    women: "https://images.pexels.com/photos/32695898/pexels-photo-32695898.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    confident: "https://images.pexels.com/photos/4498294/pexels-photo-4498294.jpeg?auto=compress&cs=tinysrgb&w=1200",
    studio: "https://images.unsplash.com/photo-1783841193384-4269c68aa050?crop=entropy&cs=srgb&fm=jpg&q=85",
    chandelier: "https://images.pexels.com/photos/14793731/pexels-photo-14793731.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    nutrition: "https://images.unsplash.com/photo-1482049016688-2d3e1b311543?crop=entropy&cs=srgb&fm=jpg&q=85",
    yoga: "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?crop=entropy&cs=srgb&fm=jpg&q=85",
    pilates: "https://images.unsplash.com/photo-1518611012118-696072aa579a?crop=entropy&cs=srgb&fm=jpg&q=85",
    studio1: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    studio2: "https://images.unsplash.com/photo-1518611012118-696072aa579a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    studio3: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const WHATSAPP_NUMBER = "917032690303";
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_eve-fitness-hub/artifacts/mpla7sb8_AAE%20Logo.webp";
const LOGO_ICON_URL = "https://customer-assets-agu9un31.emergentagent.net/job_eve-fitness-hub/artifacts/qh33w294_Just%20Logo%20act.png";

const BrandLogo = ({ className = "h-14 md:h-16" }) => (
    <span
        className="relative inline-flex items-center justify-center px-3 py-1.5 rounded-2xl overflow-hidden shrink-0 group"
        style={{
            background: "linear-gradient(135deg, #FFFFFF 0%, #FFE7EB 45%, #DC143C 100%)",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.6), 0 10px 30px rgba(220,20,60,0.25)",
        }}
    >
        <span
            aria-hidden
            className="absolute inset-y-0 -left-1/2 w-1/2 opacity-70 group-hover:animate-shimmer pointer-events-none"
            style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)" }}
        />
        <span aria-hidden className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white/40 blur-2xl" />
        <img
            src={LOGO_URL}
            alt="All About Eve Fitness"
            className={`${className} w-auto object-contain select-none relative z-10`}
            draggable={false}
        />
    </span>
);

const BrandMark = ({ size = 40 }) => (
    <img
        src={LOGO_ICON_URL}
        alt="All About Eve"
        className="object-contain rounded-md bg-ivory p-0.5"
        style={{ width: size, height: size }}
        draggable={false}
    />
);

const MemberLoginModal = ({ open, onClose }) => {
    // tabs: "password" (default returning users) | "otp" (first-time + fallback)
    const [tab, setTab] = useState("password");
    // "login" (regular OTP sign-in) | "reset" (OTP → new password)
    const [otpMode, setOtpMode] = useState("login");
    // OTP sub-step: 1 = enter details, 2 = enter code
    const [step, setStep] = useState(1);

    const [pw, setPw] = useState({ email: "", password: "" });
    const [f, setF] = useState({ name: "", email: "", phone: "" });
    const [otp, setOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");

    const [busy, setBusy] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [smsSending, setSmsSending] = useState(false);
    const [smsSentTo, setSmsSentTo] = useState("");

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    useEffect(() => {
        if (!open) {
            setTab("password"); setOtpMode("login"); setStep(1);
            setPw({ email: "", password: "" });
            setF({ name: "", email: "", phone: "" });
            setOtp(""); setNewPassword("");
            setCooldown(0); setSmsSentTo(""); setSmsSending(false); setBusy(false);
        }
    }, [open]);

    const passwordLogin = async () => {
        if (!pw.email.trim() || !pw.password) return toast.error("Enter email and password");
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/auth/password/login`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: pw.email.trim(), password: pw.password }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Login failed");
            toast.success(`Welcome back, ${data.name?.split(" ")[0] || "friend"}!`);
            window.location.href = "/dashboard";
        } catch (err) {
            toast.error(err.message); setBusy(false);
        }
    };

    const forgotPassword = () => {
        setTab("otp"); setOtpMode("reset"); setStep(1);
        if (pw.email.trim()) setF((p) => ({ ...p, email: pw.email.trim() }));
    };

    const sendOtp = async () => {
        // reset mode only needs email; login mode needs name + phone too
        if (otpMode === "reset") {
            if (!f.email.trim()) return toast.error("Enter your email");
        } else if (!f.name.trim() || !f.email.trim() || !f.phone.trim()) {
            return toast.error("Please share your name, email and phone.");
        }
        setBusy(true);
        try {
            const payload = otpMode === "reset"
                ? { email: f.email.trim(), name: f.name || "there", phone: f.phone || "", purpose: "reset" }
                : { ...f, purpose: "login" };
            const res = await fetch(`${API_BASE}/auth/request-otp`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Could not send code");
            toast.success(`Code sent to ${f.email}`);
            setStep(2); setCooldown(45); setOtp(""); setNewPassword("");
        } catch (err) { toast.error(err.message); }
        setBusy(false);
    };

    const sendSms = async () => {
        setSmsSending(true);
        try {
            const res = await fetch(`${API_BASE}/auth/otp/send-sms`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: f.email, phone: f.phone, purpose: otpMode }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Could not send SMS");
            setSmsSentTo(data.phone_masked || f.phone);
            setOtp(""); setCooldown(45);
            toast.success(`Code sent via SMS to ${data.phone_masked || f.phone}`);
        } catch (err) { toast.error(err.message); }
        setSmsSending(false);
    };

    const verifyLogin = async () => {
        if (otp.length !== 6) return toast.error("Enter the 6-digit code");
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/auth/verify-otp`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: f.email, otp, purpose: "login" }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Verification failed");
            toast.success(data.returning ? `Welcome back, ${f.name.split(" ")[0] || "friend"}!` : `Welcome to Eve, ${f.name.split(" ")[0] || "friend"}!`);
            window.location.href = "/dashboard";
        } catch (err) { toast.error(err.message); setBusy(false); }
    };

    const resetPassword = async () => {
        if (otp.length !== 6) return toast.error("Enter the 6-digit code");
        if (newPassword.length < 8) return toast.error("Password must be at least 8 characters");
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/auth/password/reset`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: f.email, otp, password: newPassword }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Reset failed");
            toast.success("Password updated — signing you in…");
            window.location.href = "/dashboard";
        } catch (err) { toast.error(err.message); setBusy(false); }
    };

    const title = tab === "password"
        ? "Welcome back"
        : step === 2
            ? (otpMode === "reset" ? "Reset your password" : "Enter your code")
            : (otpMode === "reset" ? "Reset password" : "Join Eve");

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="bg-obsidian-light border border-gold/25 text-ivory max-w-md" data-testid="member-login-modal">
                <DialogHeader>
                    <DialogTitle className="font-display text-3xl gold-text">{title}</DialogTitle>
                </DialogHeader>

                {step === 1 && (
                    <div className="flex border-b border-gold/20 mb-2 -mt-1" data-testid="login-tabs">
                        <button
                            onClick={() => { setTab("password"); setOtpMode("login"); }}
                            className={`flex-1 py-2 text-sm font-mono-label ${tab === "password" ? "text-gold border-b-2 border-gold" : "text-sand/60 hover:text-sand"}`}
                            data-testid="login-tab-password"
                        >
                            Password
                        </button>
                        <button
                            onClick={() => { setTab("otp"); setOtpMode("login"); }}
                            className={`flex-1 py-2 text-sm font-mono-label ${tab === "otp" ? "text-gold border-b-2 border-gold" : "text-sand/60 hover:text-sand"}`}
                            data-testid="login-tab-otp"
                        >
                            Email code
                        </button>
                    </div>
                )}

                {tab === "password" && step === 1 ? (
                    <>
                        <p className="text-sand text-sm -mt-1">
                            Welcome back. Sign in with your email and password.
                        </p>
                        <div className="space-y-3 mt-2">
                            <div><Label>Email</Label>
                                <Input type="email" value={pw.email}
                                       onChange={(e) => setPw({ ...pw, email: e.target.value })}
                                       placeholder="you@example.com" data-testid="pw-login-email" />
                            </div>
                            <div><Label>Password</Label>
                                <Input type="password" value={pw.password}
                                       onChange={(e) => setPw({ ...pw, password: e.target.value })}
                                       onKeyDown={(e) => e.key === "Enter" && passwordLogin()}
                                       placeholder="At least 8 characters" data-testid="pw-login-password" />
                            </div>
                            <Button onClick={passwordLogin} disabled={busy} className="btn-gold w-full"
                                    data-testid="pw-login-submit">
                                {busy ? "Signing in…" : "Sign in"}
                            </Button>
                            <div className="flex items-center justify-between text-xs mt-1">
                                <button onClick={forgotPassword} className="text-gold hover:underline"
                                        data-testid="pw-forgot">Forgot password?</button>
                                <button onClick={() => { setTab("otp"); setOtpMode("login"); }}
                                        className="text-sand/70 hover:text-gold" data-testid="pw-switch-otp">
                                    First time? Use an email code →
                                </button>
                            </div>
                            <button onClick={goGoogleLogin} className="w-full text-xs text-sand/70 hover:text-gold mt-2"
                                    data-testid="login-google-fallback">
                                Admin or staff? Sign in with Google →
                            </button>
                        </div>
                    </>
                ) : tab === "otp" && step === 1 ? (
                    <>
                        <p className="text-sand text-sm -mt-1">
                            {otpMode === "reset"
                                ? "Pop in the email on your account. We'll send a code to reset your password."
                                : "We'll email you a six-digit code to confirm it's you. Already on the roster? We'll quietly link your subscription."}
                        </p>
                        <div className="space-y-3 mt-2">
                            {otpMode === "login" && (
                                <div><Label>Full name</Label>
                                    <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                                           placeholder="Your name" data-testid="login-name" />
                                </div>
                            )}
                            <div><Label>Email</Label>
                                <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}
                                       placeholder="you@example.com" data-testid="login-email-input" />
                            </div>
                            {otpMode === "login" && (
                                <div><Label>Phone</Label>
                                    <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}
                                           placeholder="+91 90000 00000" data-testid="login-phone" />
                                </div>
                            )}
                            <Button onClick={sendOtp} disabled={busy} className="btn-gold w-full"
                                    data-testid="login-send-otp">
                                {busy ? "Sending…" : "Send me a code"}
                            </Button>
                            <button onClick={() => { setTab("password"); setOtpMode("login"); }}
                                    className="w-full text-xs text-sand/70 hover:text-gold mt-1"
                                    data-testid="otp-switch-password">
                                ← Have a password? Sign in with it instead
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="text-sand text-sm -mt-2">
                            {smsSentTo ? (
                                <>Six-digit code sent via <span className="text-gold">SMS</span> to <span className="text-gold">{smsSentTo}</span> &mdash; valid for ten minutes.</>
                            ) : (
                                <>Six-digit code sent to <span className="text-gold">{f.email}</span> &mdash; valid for ten minutes.</>
                            )}
                        </p>
                        <div className="space-y-3 mt-2">
                            <Input value={otp}
                                   onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                   placeholder="______" maxLength={6}
                                   className="text-center text-2xl font-mono tracking-[0.4em]"
                                   data-testid="login-otp-input" />
                            {otpMode === "reset" && (
                                <div>
                                    <Label>New password</Label>
                                    <Input type="password" value={newPassword}
                                           onChange={(e) => setNewPassword(e.target.value)}
                                           placeholder="At least 8 characters" data-testid="reset-new-password" />
                                </div>
                            )}
                            <Button onClick={otpMode === "reset" ? resetPassword : verifyLogin}
                                    disabled={busy || otp.length !== 6 || (otpMode === "reset" && newPassword.length < 8)}
                                    className="btn-gold w-full" data-testid="login-submit-button">
                                {busy
                                    ? (otpMode === "reset" ? "Resetting…" : "Verifying…")
                                    : (otpMode === "reset" ? "Reset & sign in" : "Verify & continue")}
                            </Button>
                            <div className="flex items-center justify-between text-xs mt-1">
                                <button onClick={() => { setStep(1); setOtp(""); setNewPassword(""); setSmsSentTo(""); }}
                                        className="text-sand/70 hover:text-gold"
                                        data-testid="login-change-details">← Change details</button>
                                <button onClick={sendOtp} disabled={cooldown > 0 || busy}
                                        className={`${cooldown > 0 ? "text-sand/40" : "text-gold hover:underline"}`}
                                        data-testid="login-resend-otp">
                                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                                </button>
                            </div>
                            {!smsSentTo && (
                                <button onClick={sendSms} disabled={smsSending || busy}
                                        className="w-full text-xs text-gold hover:underline mt-2 flex items-center justify-center gap-1 disabled:opacity-50"
                                        data-testid="login-sms-fallback">
                                    <MessageSquare className="w-3 h-3" />
                                    {smsSending ? "Sending SMS…" : "Didn't get the email? Send code via SMS"}
                                </button>
                            )}
                            {smsSentTo && (
                                <button onClick={sendSms} disabled={smsSending || cooldown > 0}
                                        className="w-full text-xs text-sand/70 hover:text-gold mt-2 flex items-center justify-center gap-1 disabled:opacity-50"
                                        data-testid="login-sms-resend">
                                    <MessageSquare className="w-3 h-3" />
                                    {smsSending ? "Sending…" : cooldown > 0 ? `Resend SMS in ${cooldown}s` : "Resend code via SMS"}
                                </button>
                            )}
                            <button
                                onClick={async () => {
                                    try {
                                        const res = await fetch(`${API_BASE}/auth/otp-fallback`, {
                                            method: "POST", credentials: "include",
                                            headers: { "Content-Type": "application/json" },
                                            body: JSON.stringify({ email: f.email, purpose: otpMode }),
                                        });
                                        const data = await res.json();
                                        if (!res.ok) throw new Error(data.detail || "Could not reach WhatsApp");
                                        window.open(data.whatsapp_url, "_blank");
                                    } catch (err) { toast.error(err.message); }
                                }}
                                className="w-full text-[11px] text-sand/50 hover:text-emerald-400 mt-1"
                                data-testid="login-whatsapp-fallback">
                                Still stuck? Ping us on WhatsApp →
                            </button>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

const MemberLoginCtx = React.createContext(() => {});
const useMemberLogin = () => React.useContext(MemberLoginCtx);
const BookingCtx = React.createContext(() => {});
const useBooking = () => React.useContext(BookingCtx);
const CodexCtx = React.createContext(() => {});
const useCodex = () => React.useContext(CodexCtx);

const StudioStrip = () => {
    const slides = [
        { src: IMG.studio1, label: "Strength Floor" },
        { src: IMG.studio2, label: "Reformer & Pilates" },
        { src: IMG.studio3, label: "Zumba & Yoga Hall" },
    ];
    return (
        <section id="studio" className="relative section-x pb-8 lg:pb-14">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}>
                <div className="font-mono-label mb-4">Inside the SR Nagar studio</div>
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ivory max-w-3xl mb-10">A house of mirrors, iron, and silks.</h2>
                <div className="grid md:grid-cols-3 gap-5">
                    {slides.map((s, i) => (
                        <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                            className="relative rounded-2xl overflow-hidden border border-gold/20 group" data-testid={`studio-strip-${i}`}>
                            <img src={s.src} alt={s.label} className="w-full h-72 object-cover transition-transform duration-700 group-hover:scale-105" />
                            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/10 to-transparent" />
                            <div className="absolute bottom-4 left-4 right-4">
                                <div className="font-mono-label !text-[10px] text-gold">/ 0{i + 1}</div>
                                <div className="font-display text-xl text-ivory mt-1">{s.label}</div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>
        </section>
    );
};

const disciplines = ["Personal Training", "Zumba", "Kickboxing", "Aerial Yoga", "Yoga", "Mat Pilates", "Reformer Pilates", "Holistic Diet Consultation", "Not sure yet"];

const TrialBookingModal = ({ open, onClose }) => {
    const [f, setF] = useState({ name: "", phone: "", email: "", discipline: "Not sure yet", message: "" });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!f.name.trim() || !f.phone.trim()) {
            toast.error("Please share your name and phone.");
            return;
        }
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/leads`, {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f),
            });
            if (!res.ok) throw new Error("Failed");
            toast.success("Booked. Our team will call you shortly.");
            setF({ name: "", phone: "", email: "", discipline: "Not sure yet", message: "" });
            onClose();
        } catch {
            toast.error("Could not submit. Please call +91 70326 90303.");
        }
        setBusy(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="bg-obsidian-light border border-gold/25 text-ivory max-w-lg">
                <DialogHeader>
                    <DialogTitle className="font-display text-3xl">Book a trial visit or a callback</DialogTitle>
                </DialogHeader>
                <p className="text-sand text-sm -mt-2">Ten minutes on the phone or an in-person tour of the floor &mdash; no credit card, no commitment, no upsell.</p>
                <div className="space-y-3 mt-2">
                    <div><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" data-testid="lead-name" /></div>
                    <div><Label>Phone (+91)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+91 90000 00000" data-testid="lead-phone" /></div>
                    <div><Label>Email (optional)</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="lead-email" /></div>
                    <div>
                        <Label>Preferred discipline</Label>
                        <Select value={f.discipline} onValueChange={(v) => setF({ ...f, discipline: v })}>
                            <SelectTrigger data-testid="lead-discipline"><SelectValue /></SelectTrigger>
                            <SelectContent>{disciplines.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div><Label>Anything to share? (optional)</Label><Textarea rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="lead-message" /></div>
                </div>
                <div className="flex gap-3 mt-2">
                    <Button onClick={submit} disabled={busy} className="btn-gold flex-1" data-testid="lead-submit"><Send className="w-4 h-4 mr-2" /> {busy ? "Booking…" : "Confirm booking"}</Button>
                    <Button variant="ghost" onClick={onClose} className="text-sand" data-testid="lead-cancel">Cancel</Button>
                </div>
                <div className="text-xs text-sand/70 text-center mt-2">Prefer voice? <a className="text-gold" href="tel:+917032690303">+91 70326 90303</a></div>
            </DialogContent>
        </Dialog>
    );
};

const CodexCheckoutModal = ({ open, onClose }) => {
    const [f, setF] = useState({ name: "", email: "", phone: "", plan: "lifetime" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(null);
    const [otpStep, setOtpStep] = useState(false);
    const [otp, setOtp] = useState("");
    const [verified, setVerified] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);
    useEffect(() => {
        if (!open) { setOtpStep(false); setOtp(""); setVerified(false); setCooldown(0); }
    }, [open]);

    const plans = {
        lifetime: { label: "Lifetime access", price: "\u20B9899", sub: "One payment \u00b7 all four phase editions, forever" },
        monthly: { label: "Monthly pass", price: "\u20B999", sub: "One month \u00b7 a single phase edition" },
    };

    const sendOtp = async () => {
        if (!f.name.trim() || !f.email.trim() || !f.phone.trim()) {
            toast.error("Please share your name, email and phone."); return;
        }
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/auth/request-otp`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...f, purpose: "codex" }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Could not send code");
            toast.success(`Code sent to ${f.email}`);
            setOtpStep(true); setCooldown(45);
        } catch (err) { toast.error(err.message); }
        setBusy(false);
    };

    const verifyOtp = async () => {
        if (otp.length !== 6) return toast.error("Enter the 6-digit code");
        setBusy(true);
        try {
            const res = await fetch(`${API_BASE}/auth/verify-otp`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: f.email, otp, purpose: "codex" }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Verification failed");
            toast.success("Email verified — proceeding to payment");
            setVerified(true); setOtpStep(false);
            await pay();
        } catch (err) {
            toast.error(err.message); setBusy(false);
        }
    };

    const pay = async () => {
        if (typeof window.Razorpay !== "function") {
            toast.error("Razorpay didn't load. Refresh the page and try again."); return;
        }
        setBusy(true);
        try {
            const orderRes = await fetch(`${API_BASE}/codex/order`, {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f),
            });
            if (!orderRes.ok) throw new Error("Order create failed");
            const order = await orderRes.json();

            const rzp = new window.Razorpay({
                key: order.key_id,
                amount: order.amount,
                currency: order.currency,
                order_id: order.order_id,
                name: "All About Eve Fitness",
                description: order.label,
                image: "https://customer-assets-agu9un31.emergentagent.net/job_eve-fitness-hub/artifacts/qh33w294_Just%20Logo%20act.png",
                prefill: { name: f.name, email: f.email, contact: f.phone },
                theme: { color: "#D4AF37" },
                notes: { product: "The Eve Codex", plan: f.plan },
                modal: { ondismiss: () => setBusy(false) },
                handler: async (r) => {
                    try {
                        const v = await fetch(`${API_BASE}/codex/verify`, {
                            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({
                                razorpay_order_id: r.razorpay_order_id,
                                razorpay_payment_id: r.razorpay_payment_id,
                                razorpay_signature: r.razorpay_signature,
                            }),
                        });
                        if (!v.ok) throw new Error("Verify failed");
                        toast.success("Payment received \u2014 welcome to the Codex.");
                        setDone({ payment_id: r.razorpay_payment_id, plan: f.plan });
                    } catch (err) {
                        toast.error("Payment captured, verification pending \u2014 we'll reconcile. In the meantime, +91 70326 90303.");
                        if (process.env.NODE_ENV !== "production") console.error(err);
                    }
                    setBusy(false);
                },
            });
            rzp.on("payment.failed", (resp) => {
                toast.error("Payment failed: " + (resp.error?.description || "please try again"));
                setBusy(false);
            });
            rzp.open();
        } catch (err) {
            if (process.env.NODE_ENV !== "production") console.error(err);
            toast.error("Could not start payment. Please try again in a moment.");
            setBusy(false);
        }
    };

    const close = () => { setDone(null); setF({ name: "", email: "", phone: "", plan: "lifetime" }); onClose(); };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && close()}>
            <DialogContent className="bg-obsidian-light border border-gold/25 text-ivory max-w-lg">
                {done ? (
                    <>
                        <DialogHeader><DialogTitle className="font-display text-3xl gold-text">Welcome to the Codex.</DialogTitle></DialogHeader>
                        <p className="text-sand mt-1">Payment ID <span className="font-mono text-gold text-xs">{done.payment_id}</span> confirmed.</p>
                        <div className="rounded-xl border border-gold/20 p-5 mt-3 space-y-2">
                            <div className="font-mono-label">What&apos;s next</div>
                            <p className="text-sand text-sm">We&apos;ve sent your Codex download link to <span className="text-gold">{f.email}</span>. It stays live for thirty days and belongs only to this purchase.</p>
                        </div>
                        <div className="text-[11px] text-sand/60 mt-2">Lifetime access unlocks all four phase editions, forever. Monthly unlocks one.</div>
                        <Button onClick={close} className="btn-gold w-full mt-4" data-testid="codex-done-btn">Done</Button>
                    </>
                ) : (
                    <>
                        <DialogHeader><DialogTitle className="font-display text-3xl">Unlock The Eve Codex</DialogTitle></DialogHeader>
                        <p className="text-sand text-sm -mt-2">One secure Razorpay checkout &mdash; cards, UPI, wallets, netbanking.</p>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                            {Object.entries(plans).map(([key, p]) => (
                                <button
                                    key={key}
                                    onClick={() => setF({ ...f, plan: key })}
                                    className={`rounded-xl border p-4 text-left transition-colors ${f.plan === key ? "border-gold bg-gold/10" : "border-gold/25 hover:border-gold/60"}`}
                                    data-testid={`plan-${key}`}
                                >
                                    <div className="font-mono-label !text-[9px] text-gold">{p.label}</div>
                                    <div className={`font-display text-2xl mt-1 ${f.plan === key ? "gold-text" : "text-ivory"}`}>{p.price}</div>
                                    <div className="text-xs text-sand/80 mt-1">{p.sub}</div>
                                </button>
                            ))}
                        </div>
                        <div className="space-y-3 mt-3">
                            <div><Label>Full name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" data-testid="codex-name" /></div>
                            <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" data-testid="codex-email" /></div>
                            <div><Label>Phone (+91)</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+91 90000 00000" data-testid="codex-phone" /></div>
                        </div>
                        <div className="flex gap-3 mt-2">
                            <Button onClick={pay} disabled={busy} className="btn-gold flex-1" data-testid="codex-pay-btn">
                                <Sparkles className="w-4 h-4 mr-2" /> {busy ? "Redirecting…" : `Pay ${plans[f.plan].price} securely`}
                            </Button>
                            <Button variant="ghost" onClick={close} className="text-sand" data-testid="codex-cancel">Cancel</Button>
                        </div>
                        <div className="text-[11px] text-sand/60 text-center mt-1">Razorpay-secured &middot; SSL-encrypted &middot; GST receipt on WhatsApp</div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

const WhatsAppFloat = () => {
    const msg = encodeURIComponent("Hi — I'd like to know more about All About Eve Fitness.");
    return (
        <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`}
            target="_blank"
            rel="noreferrer"
            data-testid="whatsapp-float"
            className="fixed bottom-6 right-6 z-40 group flex items-center gap-3"
            aria-label="Chat on WhatsApp"
        >
            <span className="hidden sm:block bg-obsidian-light/95 backdrop-blur border border-gold/25 rounded-full px-4 py-2 text-sm text-ivory shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">Chat with reception</span>
            <span className="relative w-14 h-14 rounded-full grid place-items-center shadow-[0_10px_30px_rgba(37,211,102,0.35)] transition-transform duration-300 group-hover:scale-110" style={{ background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)" }}>
                <span className="absolute inset-0 rounded-full animate-ping bg-emerald-400/40" />
                <MessageCircle className="w-6 h-6 text-white relative z-10" strokeWidth={2.2} fill="white" />
            </span>
        </a>
    );
};

const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const openLogin = useMemberLogin();
    useEffect(() => {
        const on = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", on); return () => window.removeEventListener("scroll", on);
    }, []);
    return (
        <nav className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "bg-obsidian/85 backdrop-blur-xl border-b border-gold/15" : "bg-transparent"}`} data-testid="landing-nav">
            <div className="section-x flex items-center justify-between h-24">
                <a href="#" className="flex items-center gap-3" data-testid="brand-link">
                    <BrandLogo className="h-14 md:h-16" />
                    <div className="font-mono-label !text-[10px] hidden lg:block border-l border-gold/25 pl-3">Hyderabad · Est. 2018</div>
                </a>
                <div className="hidden md:flex items-center gap-8 text-sm text-sand">
                    <a href="#story" className="hover:text-gold transition-colors" data-testid="nav-story">Philosophy</a>
                    <a href="#usp" className="hover:text-gold transition-colors" data-testid="nav-usp">Cycle Sync</a>
                    <a href="#programs" className="hover:text-gold transition-colors" data-testid="nav-programs">All Programs</a>
                    <a href="#membership" className="hover:text-gold transition-colors" data-testid="nav-membership">Membership</a>
                    <a href="#contact" className="hover:text-gold transition-colors" data-testid="nav-contact">Visit</a>
                </div>
                <button onClick={openLogin} className="btn-gold text-sm" data-testid="nav-login-btn">Member Login</button>
            </div>
        </nav>
    );
};

const Hero = () => {
    const openBooking = useBooking();
    const { scrollY } = useScroll();
    const y = useTransform(scrollY, [0, 600], [0, 120]);
    const opacity = useTransform(scrollY, [0, 400], [1, 0.4]);
    return (
        <section className="relative min-h-[100vh] overflow-hidden grain" data-testid="hero-section">
            <motion.div style={{ y }} className="absolute inset-0">
                <img src={IMG.hero} alt="" className="w-full h-full object-cover opacity-40" />
                <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/40 to-obsidian" />
                <div className="absolute -left-32 top-1/3 w-[420px] h-[420px] rounded-full bg-crimson/15 blur-3xl" />
                <div className="absolute -right-24 bottom-10 w-[380px] h-[380px] rounded-full bg-gold/15 blur-3xl" />
            </motion.div>
            <motion.div style={{ opacity }} className="relative section-x pt-40 pb-24 max-w-6xl">
                <div className="flex items-center gap-3 mb-6">
                    <span className="w-8 h-px bg-gold/60" />
                    <div className="font-mono-label">Hyderabad · Women-only atelier · Est. 2018</div>
                </div>
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-ivory">
                    Every workout. Every meal. <br />
                    <span className="gold-text italic">Tuned to your cycle.</span><br />
                    A whole studio, quietly built for <span className="text-crimson">you</span>.
                </h1>
                <p className="mt-8 max-w-2xl text-lg text-sand">
                    All About Eve is Hyderabad&apos;s women-only atelier for strength, movement, and nourishment —
                    one address, every discipline, every plate, every class quietly reshaped around the week
                    you&apos;re actually in.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                    <button onClick={openBooking} className="btn-gold" data-testid="hero-cta-primary">
                        Book a trial visit <ChevronRight className="inline w-4 h-4 ml-1" />
                    </button>
                    <a href="#usp" className="btn-ghost-gold" data-testid="hero-cta-secondary">See how cycle-syncing works</a>
                </div>
                <div className="mt-16 flex flex-wrap items-center gap-8 text-sand/80 text-sm">
                    <div><div className="text-3xl font-display gold-text flex items-center gap-2">4.4<Star className="w-5 h-5 fill-gold text-gold" /></div><div>112+ Google reviews</div></div>
                    <div className="w-px h-8 bg-gold/25 hidden md:block" />
                    <div><span className="text-3xl font-display text-gold">8</span><div>Disciplines under one roof</div></div>
                    <div className="w-px h-8 bg-gold/25 hidden md:block" />
                    <div><span className="text-3xl font-display text-gold">100%</span><div>Women-only floor</div></div>
                </div>
            </motion.div>
        </section>
    );
};

const Section = ({ id, eyebrow, title, kicker, children }) => (
    <section id={id} className="relative section-x py-24 lg:py-32">
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.7 }}>
            {eyebrow && <div className="font-mono-label mb-4">{eyebrow}</div>}
            {title && <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-ivory max-w-3xl mb-4">{title}</h2>}
            {kicker && <p className="text-sand text-lg max-w-2xl mb-10">{kicker}</p>}
            {children}
        </motion.div>
    </section>
);

const Story = () => (
    <Section
        id="story"
        eyebrow="The Philosophy"
        title="A sanctuary for your strength — nothing less."
        kicker="Founded in 2018, All About Eve became Hyderabad's first address built — without compromise, without the male gaze, without small talk about calories — for women, by women. Only possibility."
    >
        <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7 space-y-6 text-sand text-lg leading-relaxed">
                <p>You shouldn&apos;t have to gym-hop between a yoga studio, a Zumba hall, a dietician&apos;s clinic, and a weights floor. We fold <span className="text-gold">every discipline you need — and the nutritionist you&apos;ll actually trust — into one women-only address in SR Nagar</span>.</p>
                <p>And no, we don&apos;t coach you the same way every week — your body wasn&apos;t built on a loop. <span className="text-crimson">We programme your training and plate your food around your menstrual cycle</span>, so you move with your hormones instead of fighting them for progress.</p>
            </div>
            <div className="lg:col-span-5 relative">
                <img src={IMG.confident} alt="" className="rounded-2xl border border-gold/20 object-cover w-full h-[420px]" />
                <div className="absolute -bottom-6 -left-6 card-noir p-5 max-w-[220px] hidden md:block">
                    <div className="font-mono-label">Google rating</div>
                    <div className="font-display text-3xl gold-text flex items-center gap-2">4.4<Star className="w-5 h-5 fill-gold text-gold" /></div>
                    <div className="text-xs text-sand/80">Verified members · Hyderabad</div>
                </div>
            </div>
        </div>
    </Section>
);

const cyclePhases = [
    { name: "Menstrual", d: "Day 1–5", train: "Restorative yoga · gentle mat pilates", eat: "Iron-rich · warm, slow-cooked · magnesium", color: "crimson" },
    { name: "Follicular", d: "Day 6–13", train: "Heavy strength · kickboxing · learn new skills", eat: "Fresh · fermented · light proteins", color: "gold" },
    { name: "Ovulatory", d: "Day 14–16", train: "HIIT · Zumba · aerial yoga peaks", eat: "Anti-inflammatory · hydrating · fibre-rich", color: "gold" },
    { name: "Luteal", d: "Day 17–28", train: "Reformer pilates · steady lifting", eat: "Complex carbs · B-vitamins · unapologetic comfort", color: "crimson" },
];

const CycleUSP = () => {
    const openBooking = useBooking();
    return (
    <Section
        id="usp"
        eyebrow="The Signature · Phase-synced training & nutrition"
        title="The only Hyderabad studio that programmes your body around your cycle."
        kicker="Every phase quietly rewrites what your body can do — and what it craves. Our coaches build your week, our nutritionist builds your plate, and the two meet exactly where your cycle is this week."
    >
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {cyclePhases.map((p, i) => (
                <motion.div key={p.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                    className="card-noir p-6 flex flex-col" data-testid={`phase-${p.name.toLowerCase()}`}>
                    <div className={`font-mono-label ${p.color === "gold" ? "text-gold" : "text-crimson"}`}>{p.d}</div>
                    <div className="font-display text-2xl text-ivory mt-2">{p.name}</div>
                    <div className="mt-5 space-y-3 text-sm">
                        <div>
                            <div className="font-mono-label !text-[9px]">Train</div>
                            <div className="text-sand mt-1">{p.train}</div>
                        </div>
                        <div>
                            <div className="font-mono-label !text-[9px]">Eat</div>
                            <div className="text-sand mt-1">{p.eat}</div>
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
        <div className="mt-10 flex items-center gap-4 flex-wrap">
            <button onClick={openBooking} className="btn-crimson" data-testid="cycle-cta">Sync my cycle</button>
            <span className="text-sand/70 text-sm">Included — quietly — with every membership.</span>
        </div>
    </Section>
    );
};

const programs = [
    { icon: Dumbbell, title: "Personal Training", desc: "Progressive strength and body composition, one-on-one with coaches who have been exactly where you want to go." },
    { icon: Zap, title: "Zumba", desc: "High-energy dance cardio choreographed to Bollywood and Latin sets — technically a workout, practically a party." },
    { icon: HandHeart, title: "Kickboxing", desc: "Explosive power, surgical technique, and the kind of confidence you can't fake with a filter." },
    { icon: Waves, title: "Aerial Yoga", desc: "Silk-supported inversions for a longer spine, a stronger core, and the occasional feeling of levitation." },
    { icon: Flower2, title: "Yoga", desc: "Hatha, Vinyasa, and restorative — every class quietly adjusted for the phase you're in." },
    { icon: Sparkles, title: "Mat Pilates", desc: "Deep-core precision work — sculpted lines, resilient muscle, zero posturing." },
    { icon: HeartPulse, title: "Reformer Pilates", desc: "Spring-resistance work — the gold standard for postural strength and the back your 40s will thank you for." },
    { icon: Salad, title: "Holistic Diet Consultation", desc: "An in-house nutritionist who writes cycle-synced Indian meal plans your family will happily eat too." },
];

const Programs = () => (
    <Section
        id="programs"
        eyebrow="The Disciplines"
        title="Eight disciplines. One women-only address."
        kicker="From heavy iron to gentle silks, Bollywood Zumba to a nutritionist who actually plans your thali — the only thing you'll leave with is a reason to come back tomorrow."
    >
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {programs.map((p, i) => (
                <motion.div key={p.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                    className="card-noir p-7 hover:-translate-y-1.5 transition-transform duration-300" data-testid={`program-${i}`}>
                    <p.icon className={`w-8 h-8 mb-5 ${i % 3 === 1 ? "text-crimson" : "text-gold"}`} strokeWidth={1.4} />
                    <h3 className="font-display text-xl text-ivory mb-2">{p.title}</h3>
                    <p className="text-sm text-sand/85">{p.desc}</p>
                    <div className="mt-5 font-mono-label !text-[10px]">/ 0{i + 1}</div>
                </motion.div>
            ))}
        </div>
    </Section>
);

const tiers = [
    {
        name: "Eve Gold",
        tagline: "The everyday sanctuary",
        perks: [
            "Run-of-house access to all eight disciplines — walk in any day",
            "A self-guided, cycle-synced class calendar in your pocket",
            "Locker, towel, and tea service — daily",
            "Eve, our in-house AI, on tap for the two-AM questions",
        ],
        featured: false, accent: "gold",
    },
    {
        name: "Eve Diamond",
        tagline: "Guided transformation",
        perks: [
            "Everything in Gold, plus a coach who owns your results — not just your sessions",
            "Two personal training sessions each week, booked around your calendar",
            "A monthly one-on-one with the nutritionist, and a plan you'll actually follow",
            "Body composition scans and quarterly goal reviews — written down, not vibed",
        ],
        featured: true, accent: "gold",
    },
    {
        name: "Eve Empress VIP",
        tagline: "Private concierge",
        perks: [
            "Everything in Diamond, delivered white-glove",
            "A dedicated personal coach — same face, every session, no roulette",
            "Bespoke cycle-synced meal plans and a curated supplement pack",
            "Priority slots, a private changing suite, and a concierge on WhatsApp who already knows your name",
        ],
        featured: false, accent: "crimson",
    },
];

const TEL_PRIMARY = "+917032690303";
const TEL_SECONDARY = "+917032310303";

const Membership = () => {
    const openBooking = useBooking();
    const openCodex = useCodex();
    return (
    <Section
        id="membership"
        eyebrow="Membership"
        title="Three ways to live with Eve."
        kicker="Every tier includes cycle-synced coaching and run-of-house studio access. Speak with our team — we'll quietly match you to the one that fits."
    >
        <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((t, i) => (
                <motion.div
                    key={t.name}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className={`card-noir p-8 relative flex flex-col ${t.featured ? "border-gold/60" : ""}`}
                    data-testid={`tier-${i}`}
                >
                    {t.featured && (
                        <div className="absolute -top-3 left-6 font-mono-label bg-gold text-obsidian px-3 py-1 rounded-full">
                            Most chosen
                        </div>
                    )}
                    <div className={`font-mono-label ${t.accent === "gold" ? "text-gold" : "text-crimson"}`}>
                        {t.name}
                    </div>
                    <div className={`mt-3 font-display text-3xl ${t.featured ? "gold-text" : "text-ivory"}`}>
                        {t.tagline}
                    </div>
                    <div className="flex-1" />
                    <button
                        onClick={openBooking}
                        className={`mt-8 w-full ${t.featured ? "btn-gold" : "btn-ghost-gold"}`}
                        data-testid={`tier-call-${i}`}
                    >
                        Talk to our team
                    </button>
                </motion.div>
            ))}
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 card-noir p-8 lg:p-10 grid gap-4 items-start relative overflow-hidden">
                <div className="absolute -right-24 -top-16 w-72 h-72 rounded-full bg-crimson/15 blur-3xl" />
                <div className="absolute -left-20 -bottom-16 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
                <div className="relative">
                    <div className="font-mono-label">Can&apos;t decide?</div>
                    <h3 className="font-display text-3xl lg:text-4xl text-ivory mt-3 leading-tight">
                        Set up a call, or walk the floor with us &mdash; we&apos;ll pick for you.
                    </h3>
                    <p className="text-sand mt-4 max-w-xl">
                        Ten minutes with our head coach, or a quiet tour of SR Nagar &mdash; whichever feels right.
                        No credit card. No commitment. No upsell.
                    </p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 relative">
                    <a href={`tel:${TEL_PRIMARY}`} className="btn-gold text-center" data-testid="cta-call-primary">
                        <PhoneCall className="inline w-4 h-4 mr-2" /> Call {TEL_PRIMARY.replace("+91", "+91 ")}
                    </a>
                    <button onClick={openBooking} className="btn-crimson" data-testid="cta-visit">
                        <CalendarCheck className="inline w-4 h-4 mr-2" /> Book a studio visit
                    </button>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="lg:col-span-5 relative overflow-hidden rounded-2xl p-8 lg:p-10 group"
                style={{
                    background: "radial-gradient(circle at 20% 0%, rgba(220,20,60,0.28) 0%, transparent 45%), radial-gradient(circle at 100% 100%, rgba(212,175,55,0.24) 0%, transparent 50%), linear-gradient(160deg, rgba(9,8,10,0.94) 0%, rgba(18,16,22,0.96) 100%)",
                    border: "1px solid rgba(212,175,55,0.55)",
                    boxShadow: "0 30px 90px rgba(220,20,60,0.28), 0 0 0 1px rgba(212,175,55,0.18) inset",
                }}
                data-testid="playbook-card"
            >
                <div className="absolute top-6 -right-14 rotate-45 bg-crimson text-ivory text-[10px] tracking-[3px] font-semibold px-14 py-1 shadow-lg z-10">
                    LAUNCH &middot; SAVE 66%
                </div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-gold/40 bg-obsidian/60 backdrop-blur">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="font-mono-label !text-[9px]">In stock &middot; delivered to your inbox in 60s</span>
                </div>
                <div className="font-mono-label text-crimson mt-4">NEW &nbsp;&middot;&nbsp; The Eve Codex</div>
                <h3 className="font-display text-4xl lg:text-5xl mt-3 text-ivory leading-[1.05]">
                    A pocket coach for every phase of your cycle.
                </h3>
                <p className="text-sand mt-4 leading-relaxed">
                    Six coach-authored pages of <span className="text-gold">workouts and Hyderabadi meal plans</span>, quietly phase-matched to your hormones. In your inbox the moment the receipt lands.
                </p>
                <ul className="mt-5 space-y-2.5 text-sm">
                    {[
                        ["Menstrual", "a recovery flow and an iron protocol"],
                        ["Follicular", "a strength PR window and bright, fresh plates"],
                        ["Ovulatory", "peak-intensity HIIT and anti-inflammatory food"],
                        ["Luteal", "a deload strategy and the PMS relief stack we actually use"],
                    ].map(([phase, note]) => (
                        <li key={phase} className="flex items-start gap-3 text-ivory">
                            <span className="mt-0.5 w-4 h-4 rounded-full bg-gold/20 border border-gold/60 flex items-center justify-center text-gold text-[10px] leading-none flex-shrink-0">&#10003;</span>
                            <span><span className="text-gold font-semibold">{phase}</span> <span className="text-sand">&mdash; {note}</span></span>
                        </li>
                    ))}
                </ul>
                <div className="mt-7 flex items-end gap-3 flex-wrap">
                    <span className="text-sand/50 text-xl line-through">&#8377;2,599</span>
                    <span className="font-display text-5xl gold-text leading-none">&#8377;899</span>
                    <span className="text-sand text-sm pb-1">lifetime &middot; one payment</span>
                </div>
                <div className="text-xs text-sand/70 mt-1">or start at &#8377;99/month &middot; cancel on any phase</div>
                <button
                    onClick={openCodex}
                    className="btn-gold w-full mt-6 !py-4 !text-base tracking-wide relative overflow-hidden"
                    style={{ boxShadow: "0 0 40px rgba(212,175,55,0.4)" }}
                    data-testid="playbook-cta"
                >
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                    <Sparkles className="inline w-4 h-4 mr-2" /> Unlock the Codex &mdash; instant PDF
                </button>
                <div className="grid grid-cols-3 gap-2 mt-4 text-[10px] text-sand/75 text-center">
                    <div className="flex items-center justify-center gap-1.5"><span className="w-1 h-1 rounded-full bg-emerald-400" /> Razorpay-secured</div>
                    <div className="flex items-center justify-center gap-1.5"><span className="w-1 h-1 rounded-full bg-emerald-400" /> Inbox in 60s</div>
                    <div className="flex items-center justify-center gap-1.5"><span className="w-1 h-1 rounded-full bg-emerald-400" /> 7-day refund</div>
                </div>
                <div className="text-[11px] text-sand/70 mt-4 flex items-center justify-center gap-2">
                    <span className="flex -space-x-2">
                        {["A", "R", "Z", "M"].map((c) => (
                            <span key={c} className="w-5 h-5 rounded-full border border-gold/50 bg-gradient-to-br from-crimson/50 to-gold/40 text-[9px] flex items-center justify-center text-ivory font-semibold">{c}</span>
                        ))}
                    </span>
                    <span>Joined by <span className="text-gold font-semibold">340+ women</span> &middot; Diamond &amp; Empress members get theirs on the house.</span>
                </div>
            </motion.div>
        </div>
    </Section>
    );
};

const Testimonials = () => {
    const items = [
        { q: "The only place in Hyderabad that trained me around my period — not against it.", a: "Ananya · Diamond member" },
        { q: "Yoga, pilates and my diet plan under one roof. It changed how I move through my own life.", a: "Ritika · Empress VIP" },
        { q: "Zumba on Wednesday, strength on Saturday, and a plate that actually fits my cycle. Quietly genius.", a: "Sana · Gold member" },
    ];
    return (
        <Section eyebrow="In Their Words">
            <div className="grid md:grid-cols-3 gap-6">
                {items.map((t, i) => (
                    <div key={t.a} className="card-noir p-7" data-testid={`testimonial-${i}`}>
                        <div className="text-gold text-3xl font-display leading-none">&ldquo;</div>
                        <p className="mt-3 text-ivory font-display text-xl leading-snug">{t.q}</p>
                        <div className="mt-5 font-mono-label !text-[10px]">{t.a}</div>
                    </div>
                ))}
            </div>
        </Section>
    );
};

const Contact = () => (
    <Section id="contact" eyebrow="Visit" title="Come in. Quietly.">
        <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-7 card-noir p-8 space-y-5">
                <div className="flex gap-3 items-start"><MapPin className="text-gold w-5 h-5 mt-1" /><div><div className="font-display text-xl text-ivory">The address</div><div className="text-sand">2nd Floor, Ratandeep Supermarket Building, 47/3RT, SR Nagar Main Road, Hyderabad &mdash; 500038</div></div></div>
                <div className="flex gap-3 items-start"><Phone className="text-gold w-5 h-5 mt-1" /><div><div className="font-display text-xl text-ivory">The front desk</div><div className="text-sand"><a href="tel:+917032690303" className="hover:text-gold transition-colors">+91 70326 90303</a> &middot; <a href="tel:+917032310303" className="hover:text-gold transition-colors">+91 70323 10303</a></div><div className="text-sand/60 text-xs mt-1">Voice calls only &mdash; WhatsApp calls are not attended.</div></div></div>
                <div className="flex gap-3 items-start"><Clock className="text-gold w-5 h-5 mt-1" /><div><div className="font-display text-xl text-ivory">Hours</div><div className="text-sand">Mon &ndash; Sat &middot; 6:30 AM &ndash; 12:00 PM &middot; 3:00 PM &ndash; 8:00 PM</div><div className="text-sand/60 text-xs mt-1">Sundays are ours.</div></div></div>
                <div className="flex gap-3 items-start"><Instagram className="text-gold w-5 h-5 mt-1" /><div><div className="font-display text-xl text-ivory"><a href="https://instagram.com/allaboutevefitness" target="_blank" rel="noreferrer" className="hover:text-gold transition-colors" data-testid="ig-link">@allaboutevefitness</a></div><div className="text-sand">Daily dispatches from the floor &mdash; coach takeovers, honest transformations, phase-tuned workouts.</div></div></div>
                <div className="flex gap-3 pt-2 flex-wrap">
                    <a href="tel:+917032690303" className="btn-gold" data-testid="contact-cta"><PhoneCall className="inline w-4 h-4 mr-2" /> Call to book a visit</a>
                    <a className="btn-ghost-gold" href="https://www.google.com/maps/search/?api=1&query=All+About+Eve+Fitness+SR+Nagar+Hyderabad" target="_blank" rel="noreferrer" data-testid="contact-map">Open in Google Maps</a>
                </div>
            </div>
            <div className="lg:col-span-5 relative rounded-2xl overflow-hidden border border-gold/20">
                <img src="https://images.pexels.com/photos/4498151/pexels-photo-4498151.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200" alt="Women friends laughing at All About Eve" className="w-full h-full object-cover min-h-[420px] object-center" />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                    <div className="font-mono-label mb-1">Inside the sanctuary</div>
                    <div className="font-display text-2xl text-ivory">Where Hyderabad&apos;s women train, laugh, and quietly become themselves.</div>
                </div>
            </div>
        </div>
    </Section>
);

const Footer = () => (
    <footer className="section-x py-10 border-t border-gold/15 text-sand/70 text-sm flex flex-wrap gap-4 justify-between">
        <div className="flex items-center gap-3">
            <BrandLogo className="h-10" />
            <span>© {new Date().getFullYear()} · SR Nagar, Hyderabad · Since 2018</span>
        </div>
        <div className="font-mono-label">Made in silk and steel</div>
    </footer>
);

export default function Landing() {
    const [bookingOpen, setBookingOpen] = useState(false);
    const [codexOpen, setCodexOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);
    const openBooking = () => setBookingOpen(true);
    const openCodex = () => setCodexOpen(true);
    const openLogin = () => setLoginOpen(true);
    return (
        <MemberLoginCtx.Provider value={openLogin}>
        <BookingCtx.Provider value={openBooking}>
            <CodexCtx.Provider value={openCodex}>
                <div className="bg-obsidian text-ivory min-h-screen">
                    <Toaster theme="dark" richColors />
                    <Nav />
                    <Hero />
                    <Story />
                    <StudioStrip />
                    <CycleUSP />
                    <Programs />
                    <Membership />
                    <Testimonials />
                    <Contact />
                    <Footer />
                    <WhatsAppFloat />
                    <TrialBookingModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
                    <CodexCheckoutModal open={codexOpen} onClose={() => setCodexOpen(false)} />
                    <MemberLoginModal open={loginOpen} onClose={() => setLoginOpen(false)} />
                </div>
            </CodexCtx.Provider>
        </BookingCtx.Provider>
        </MemberLoginCtx.Provider>
    );
}
